import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { currentUser } from '@/lib/api'
import { useNotifications } from './useNotifications'

function fmtTime(iso: string) {
  try {
    return new Date(iso).toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit', hour12: false })
  } catch {
    return ''
  }
}

export function NotificationBell() {
  const user = currentUser.get()
  const [open, setOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  if (user?.role_name !== 'ROLE_SUPPORT') return null

  const { notifications, unreadCount, markAllRead, dismiss } = useNotifications()

  // Close panel on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (!panelRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const handleOpen = () => {
    setOpen((o) => !o)
    if (!open) markAllRead()
  }

  return (
    <>
      <div ref={panelRef} className="relative">
        {/* Bell button */}
        <button
          type="button"
          onClick={handleOpen}
          aria-label="Notifications"
          className="relative flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
          {unreadCount > 0 && (
            <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>

        {/* Dropdown panel */}
        {open && (
          <div className="absolute right-0 mt-2 w-80 rounded-xl border border-slate-200 bg-white shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <p className="text-sm font-semibold text-slate-900">Notifications</p>
              {notifications.length > 0 && (
                <button
                  type="button"
                  onClick={() => notifications.forEach((n) => dismiss(n.id))}
                  className="text-xs text-slate-400 hover:text-slate-600"
                >
                  Clear all
                </button>
              )}
            </div>

            {/* List */}
            <ul className="max-h-80 overflow-y-auto divide-y divide-slate-50">
              {notifications.length === 0 ? (
                <li className="px-4 py-8 text-center">
                  <svg className="mx-auto mb-2 h-8 w-8 text-slate-200" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                  <p className="text-sm text-slate-400">All caught up</p>
                </li>
              ) : (
                notifications.map((n) => (
                  <li key={n.id} className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50/60">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-100">
                      <svg className="h-3 w-3 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <path d="m15 9-6 6M9 9l6 6" />
                      </svg>
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold text-slate-800">{n.title}</p>
                      <p className="mt-0.5 truncate text-xs text-slate-500">{n.message}</p>
                      <p className="mt-1 text-[10px] text-slate-400">{fmtTime(n.time)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => dismiss(n.id)}
                      className="mt-0.5 shrink-0 text-slate-300 hover:text-slate-500"
                    >
                      <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <path d="M18 6 6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </li>
                ))
              )}
            </ul>

            {/* Footer */}
            <div className="border-t border-slate-100 px-4 py-2.5">
              <Link
                to="/request-logs"
                onClick={() => setOpen(false)}
                className="text-xs font-medium text-brand-600 hover:text-brand-700"
              >
                View all request logs →
              </Link>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
